import type { DailyWeatherSnapshot, EconomicArticle, PuzzlePieceRecord214, PuzzleProgress214, QuizQuestion, TodayVillageProgress, WeatherKind } from './models';
import { addIglooBricks215, iglooRate215, iglooStageForBricks, iglooStageRequirements, nextIglooRequirement, normalizeIglooProgress215 } from './iglooGrowth215';
export { iglooStageForBricks, iglooStageRequirements, nextIglooRequirement } from './iglooGrowth215';

export const TODAY_REWARD_POINTS = 23;
export const TODAY_REWARD_BRICKS = 1;
export const TODAY_REWARD_PUZZLE_PIECES = 1;
export const PUZZLE_DUPLICATES_PER_HINT = 2;

export const puzzleCatalog214 = [
  { id:'puzzle_daily_choice', type:'daily' as const, title:'오늘의 작은 선택', totalPieces:12, description:'매일 가볍게 완성하는 미니 퍼즐' },
  { id:'puzzle_weekly_saving', type:'weekly' as const, title:'저축 습관의 길', totalPieces:36, description:'한 주의 경제 습관을 연결하는 테마 퍼즐' },
  { id:'puzzle_bank_001', type:'monthly' as const, title:'설옹의 오래된 은행', totalPieces:12, description:'이야기와 기억 카드가 열리는 월간 퍼즐' },
  { id:'puzzle_season_winter', type:'seasonal' as const, title:'스노우 마운틴의 빛', totalPieces:300, description:'계절 전체를 이어 가는 그랜드 퍼즐' },
  { id:'puzzle_event_family', type:'event' as const, title:'가족 탐험대의 약속', totalPieces:24, description:'특별 미션 기간에 함께 완성하는 이벤트 퍼즐' },
] as const;

const weatherSequenceBySeason: Record<'spring'|'summer'|'autumn'|'winter', WeatherKind[]> = {
  spring:['sunny','cloudy','rain','wind','dust','sunny','cloudy'],
  summer:['rain','cloudy','heat','sunny','rain','wind','heat'],
  autumn:['sunny','cloudy','rain','wind','dust','sunny','cloudy'],
  winter:['snow','cold','cloudy','sunny','snow','wind','cold'],
};
function seasonForMonth(month: number): keyof typeof weatherSequenceBySeason {
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'autumn';
  return 'winter';
}

const weatherProfiles: Record<WeatherKind, Omit<DailyWeatherSnapshot, 'observedAt' | 'region'>> = {
  sunny: { kind:'sunny', label:'맑음', temperatureC:27, icon:'☀️', economyPrompt:'맑은 날의 여가 계획에도 예산과 기회비용이 숨어 있어요.' },
  cloudy: { kind:'cloudy', label:'흐림', temperatureC:24, icon:'☁️', economyPrompt:'흐린 날에는 계획을 다시 살피듯 소비 계획도 점검해 볼까요?' },
  rain: { kind:'rain', label:'비', temperatureC:24, icon:'🌧️', economyPrompt:'비가 많이 오면 채소 가격은 왜 달라질까요?' },
  snow: { kind:'snow', label:'눈', temperatureC:-2, icon:'🌨️', economyPrompt:'눈이 많이 오면 난방비와 배달 비용은 어떻게 달라질까요?' },
  heat: { kind:'heat', label:'폭염', temperatureC:33, icon:'🌡️', economyPrompt:'폭염이 이어지면 전기요금과 에너지 소비는 어떻게 달라질까요?' },
  cold: { kind:'cold', label:'한파', temperatureC:-8, icon:'❄️', economyPrompt:'한파에 대비하는 비용은 왜 미리 준비해야 할까요?' },
  wind: { kind:'wind', label:'강풍', temperatureC:21, icon:'💨', economyPrompt:'강풍으로 시설이 손상될 때 보험과 위험관리가 필요한 이유는 무엇일까요?' },
  dust: { kind:'dust', label:'미세먼지', temperatureC:18, icon:'🌫️', economyPrompt:'미세먼지는 개인 비용뿐 아니라 사회 전체의 비용도 만들어요.' },
};

export function dateKey(date = new Date()): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export function fallbackWeather(date = new Date(), region = '서울특별시'): DailyWeatherSnapshot {
  const key = dateKey(date).replaceAll('-', '');
  const checksum = [...key].reduce((sum, value) => sum + Number(value), 0);
  const month=date.getMonth()+1;
  const sequence=weatherSequenceBySeason[seasonForMonth(month)];
  const kind = sequence[checksum % sequence.length];
  return { ...weatherProfiles[kind], region, observedAt:new Date().toISOString() };
}

function pieceId(position: number): string {
  return `piece_${String(position).padStart(2,'0')}`;
}
function piecePosition(id: string): number {
  const parsed=Number(id.replace(/\D/g,''));
  return Number.isFinite(parsed) ? parsed : 0;
}
function pieceRarity(position: number): PuzzlePieceRecord214['rarity'] {
  if (position === 12) return 'gold';
  if (position % 4 === 0) return 'rare';
  return 'common';
}
function initialPieceSource(position: number): string {
  return ['첫 경제 탐험 완료','주간 탐험 목표 달성','가족 경제 대화 기록'][position-1] ?? '이전 활동 기록';
}

export function createPuzzleProgress214(date = new Date()): PuzzleProgress214 {
  const now=new Date(date).toISOString();
  const acquiredPieceIds=[pieceId(1),pieceId(2),pieceId(3)];
  return {
    puzzleId:'puzzle_bank_001', title:'설옹의 오래된 은행', type:'monthly', storyId:'story_bank_001', totalPieces:12,
    acquiredPieceIds,
    pieceRecords:acquiredPieceIds.map((id,index)=>({ pieceId:id, position:index+1, sourceLabel:initialPieceSource(index+1), rarity:pieceRarity(index+1), acquiredAt:now })),
    selectedPieceId:pieceId(4), hintedPieceId:'', hintTokens:1, duplicateCredits:2, storyTickets:0,
    storyUnlocked:false, completionRewardClaimed:false, completed:false, updatedAt:now,
  };
}

export function normalizePuzzleProgress(input: Partial<PuzzleProgress214> | undefined, date = new Date()): PuzzleProgress214 {
  const base=createPuzzleProgress214(date);
  if (!input) return base;
  const totalPieces=Math.max(1,Number(input.totalPieces || base.totalPieces));
  const acquiredPieceIds=Array.from(new Set((input.acquiredPieceIds || []).filter((id)=>piecePosition(id)>=1&&piecePosition(id)<=totalPieces))).slice(0,totalPieces);
  const existingRecords=Array.isArray(input.pieceRecords) ? input.pieceRecords : [];
  const pieceRecords=acquiredPieceIds.map((id)=>{
    const position=piecePosition(id);
    const found=existingRecords.find((record)=>record.pieceId===id);
    return found ?? { pieceId:id, position, sourceLabel:initialPieceSource(position), rarity:pieceRarity(position), acquiredAt:input.updatedAt || base.updatedAt };
  });
  const missing=(id?:string)=>Boolean(id&&!acquiredPieceIds.includes(id)&&piecePosition(id)>=1&&piecePosition(id)<=totalPieces);
  const completed=acquiredPieceIds.length>=totalPieces;
  return {
    ...base,
    ...input,
    totalPieces,
    acquiredPieceIds,
    pieceRecords,
    selectedPieceId:missing(input.selectedPieceId)?String(input.selectedPieceId):'',
    hintedPieceId:missing(input.hintedPieceId)?String(input.hintedPieceId):'',
    hintTokens:Math.max(0,Number(input.hintTokens ?? base.hintTokens)),
    duplicateCredits:Math.max(0,Number(input.duplicateCredits ?? base.duplicateCredits)),
    storyTickets:Math.max(0,Number(input.storyTickets ?? base.storyTickets)),
    completed,
    storyUnlocked:Boolean(input.storyUnlocked)||completed,
    completionRewardClaimed:Boolean(input.completionRewardClaimed),
  };
}

export function createTodayVillageProgress(childId: string, date = new Date()): TodayVillageProgress {
  const key = dateKey(date);
  return {
    childId,
    contentDate:key,
    status:'not-started',
    selectedChoiceId:'',
    reason:'',
    missionCompleted:false,
    rewardClaimed:false,
    points:125,
    totalBricks:7,
    todayPointsEarned:0,
    todayBricksEarned:0,
    igloo:normalizeIglooProgress215(undefined,7,new Date().toISOString()),
    puzzle:createPuzzleProgress214(date),
    updatedAt:new Date().toISOString(),
  };
}

export function ensureToday(progress: TodayVillageProgress | undefined, childId: string, date = new Date()): TodayVillageProgress {
  const key = dateKey(date);
  if (!progress) return createTodayVillageProgress(childId, date);
  const igloo=normalizeIglooProgress215(progress.igloo,progress.totalBricks,new Date().toISOString());
  const normalized={ ...progress, totalBricks:igloo.totalBricks, igloo, puzzle:normalizePuzzleProgress(progress.puzzle,date) };
  if (normalized.contentDate === key) return normalized;
  return {
    ...normalized,
    contentDate:key,
    status:'not-started',
    selectedChoiceId:'',
    reason:'',
    missionCompleted:false,
    rewardClaimed:false,
    todayPointsEarned:0,
    todayBricksEarned:0,
    updatedAt:new Date().toISOString(),
  };
}

export function selectDailyArticle(articles: EconomicArticle[], weather: DailyWeatherSnapshot, date = new Date()): EconomicArticle | undefined {
  const published = articles.filter((article) => article.status === 'published' || article.status === 'review');
  if (!published.length) return undefined;
  if (weather.kind === 'rain') {
    const rain = published.find((article) => /비|채소|날씨|공급/.test(`${article.title} ${article.summary}`));
    if (rain) return rain;
  }
  const checksum = [...dateKey(date)].reduce((sum, value) => sum + value.charCodeAt(0), 0);
  return published[checksum % published.length];
}

export function selectArticleQuestion(questions: QuizQuestion[], articleId?: string): QuizQuestion | undefined {
  return questions
    .filter((question) => question.articleId === articleId && question.active)
    .sort((a,b) => a.order - b.order)[0];
}

export function selectPuzzlePiece(puzzle: PuzzleProgress214, selectedPieceId: string): PuzzleProgress214 {
  const normalized=normalizePuzzleProgress(puzzle);
  const valid=piecePosition(selectedPieceId)>=1&&piecePosition(selectedPieceId)<=normalized.totalPieces&&!normalized.acquiredPieceIds.includes(selectedPieceId);
  if (!valid || normalized.completed) return normalized;
  return { ...normalized, selectedPieceId, updatedAt:new Date().toISOString() };
}

export function usePuzzleHint(puzzle: PuzzleProgress214): PuzzleProgress214 {
  const normalized=normalizePuzzleProgress(puzzle);
  if (normalized.completed || normalized.hintTokens<1) return normalized;
  const locked=Array.from({length:normalized.totalPieces},(_,index)=>pieceId(index+1)).filter((id)=>!normalized.acquiredPieceIds.includes(id));
  const hinted=locked.includes(normalized.selectedPieceId)?normalized.selectedPieceId:locked[0] ?? '';
  if (!hinted) return normalized;
  return { ...normalized, hintedPieceId:hinted, selectedPieceId:hinted, hintTokens:normalized.hintTokens-1, updatedAt:new Date().toISOString() };
}

export function convertPuzzleDuplicates(puzzle: PuzzleProgress214): PuzzleProgress214 {
  const normalized=normalizePuzzleProgress(puzzle);
  if (normalized.duplicateCredits<PUZZLE_DUPLICATES_PER_HINT) return normalized;
  return { ...normalized, duplicateCredits:normalized.duplicateCredits-PUZZLE_DUPLICATES_PER_HINT, hintTokens:normalized.hintTokens+1, updatedAt:new Date().toISOString() };
}

export function awardPuzzlePiece(puzzle: PuzzleProgress214, sourceLabel: string, acquiredAt = new Date().toISOString()): PuzzleProgress214 {
  const normalized=normalizePuzzleProgress(puzzle);
  const locked=Array.from({length:normalized.totalPieces},(_,index)=>pieceId(index+1)).filter((id)=>!normalized.acquiredPieceIds.includes(id));
  const awarded=locked.includes(normalized.selectedPieceId)?normalized.selectedPieceId:locked[0];
  if (!awarded) return { ...normalized, duplicateCredits:normalized.duplicateCredits+1, updatedAt:acquiredAt };
  const position=piecePosition(awarded);
  const acquiredPieceIds=[...normalized.acquiredPieceIds,awarded];
  const completed=acquiredPieceIds.length>=normalized.totalPieces;
  return {
    ...normalized,
    acquiredPieceIds,
    pieceRecords:[...normalized.pieceRecords,{ pieceId:awarded, position, sourceLabel, rarity:pieceRarity(position), acquiredAt }],
    selectedPieceId:'', hintedPieceId:'', completed, storyUnlocked:completed||normalized.storyUnlocked, updatedAt:acquiredAt,
  };
}

export function claimPuzzleCompletionReward(puzzle: PuzzleProgress214): PuzzleProgress214 {
  const normalized=normalizePuzzleProgress(puzzle);
  if (!normalized.completed || !normalized.storyUnlocked || normalized.completionRewardClaimed) return normalized;
  return { ...normalized, completionRewardClaimed:true, storyTickets:normalized.storyTickets+1, updatedAt:new Date().toISOString() };
}

export function claimTodayRewards(progress: TodayVillageProgress): TodayVillageProgress {
  if (progress.rewardClaimed) return progress;
  const nextIgloo=addIglooBricks215(progress.igloo,'ice',TODAY_REWARD_BRICKS);
  const nextBricks = nextIgloo.totalBricks;
  const nextPuzzle=awardPuzzlePiece(progress.puzzle,'오늘의 경제 탐험 완료');
  return {
    ...progress,
    status:'complete',
    rewardClaimed:true,
    points:progress.points + TODAY_REWARD_POINTS,
    totalBricks:nextBricks,
    todayPointsEarned:TODAY_REWARD_POINTS,
    todayBricksEarned:TODAY_REWARD_BRICKS,
    igloo:nextIgloo,
    puzzle:nextPuzzle,
    updatedAt:new Date().toISOString(),
  };
}

export function puzzleRate(progress: TodayVillageProgress): number {
  const puzzle=normalizePuzzleProgress(progress.puzzle);
  return Math.round((puzzle.acquiredPieceIds.length / puzzle.totalPieces) * 100);
}

export function iglooRate(progress: TodayVillageProgress): number {
  return iglooRate215(progress.igloo);
}
